import React, { useRef, useEffect } from 'react';
import { PointEntry } from '../../../../types';

interface PointsTableProps {
  pointsTable: PointEntry[];
  playerAColor: string;
  playerBColor: string;
  sponsorLogoUrl?: string;
}

export function PointsTable({ pointsTable, playerAColor, playerBColor, sponsorLogoUrl }: PointsTableProps) {
  const pointsTableRef = useRef<HTMLDivElement>(null);
  
  // Scroll automatique vers le bas quand le tableau des points est mis à jour
  useEffect(() => {
    if (pointsTableRef.current) {
      pointsTableRef.current.scrollTop = pointsTableRef.current.scrollHeight;
    }
  }, [pointsTable.length]);

  // Rendu de la colonne décision avec style selon le type et chevron pour indiquer le joueur
  const renderDecision = (point: PointEntry) => {
    const { decision, decisionFor } = point;
    if (!decision) return '';

    let bgColor = '';
    let textColor = '';

    switch (decision) {
      case 'STROKE':
        bgColor = 'bg-indigo-700';
        textColor = 'text-white';
        break;
      case 'LET':
        bgColor = 'bg-indigo-500';
        textColor = 'text-white';
        break;
      case 'NOLET':
        bgColor = 'bg-indigo-300';
        textColor = 'text-black';
        break;
      case 'CW':
        bgColor = 'bg-yellow-500';
        textColor = 'text-white';
        break;
      case 'CS':
        bgColor = 'bg-orange-500';
        textColor = 'text-white';
        break;
      case 'CG':
        bgColor = 'bg-red-500';
        textColor = 'text-white';
        break;
      // Timer decisions
      case 'EQP':
        bgColor = 'bg-cyan-500';
        textColor = 'text-white';
        break;
      case 'INJ':
        bgColor = 'bg-cyan-600';
        textColor = 'text-white';
        break;
      case 'BLD':
        bgColor = 'bg-cyan-700';
        textColor = 'text-white';
        break;
      case 'SHR':
        bgColor = 'bg-cyan-800';
        textColor = 'text-white';
        break;
      default:
        return decision;
    }

    // Chevrons pour indiquer le joueur concerné
    const leftChevron = decisionFor === 'A' ? '‹ ' : '';
    const rightChevron = decisionFor === 'B' ? ' ›' : '';

    return (
      <span className="flex items-center justify-center gap-0.5">
        {leftChevron && <span className="text-gray-500 text-[10px] font-bold">{leftChevron}</span>}
        <span className={`${bgColor} ${textColor} px-1 py-0.5 text-[10px] font-medium`}>
          {decision}
        </span>
        {rightChevron && <span className="text-gray-500 text-[10px] font-bold">{rightChevron}</span>}
      </span>
    );
  };

  return (
    <div className="bg-white flex">
      {/* Liseret gauche - Joueur A */}
      <div className="w-1" style={{ backgroundColor: playerAColor }}></div>

      {/* Tableau scrollable avec logo fixe */}
      <div className="relative flex-1 h-20 landscape:h-40 md:h-64">
        {/* Sponsor logo watermark - fixed behind scroll area */}
        {sponsorLogoUrl && (
          <div
            className="absolute inset-0 pointer-events-none opacity-15 z-0"
            style={{
              backgroundImage: `url(${sponsorLogoUrl})`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        )}
        <div
          ref={pointsTableRef}
          className="absolute inset-0 overflow-y-auto z-10"
        >
        <table className="w-full text-[10px]">
          <tbody>
            {pointsTable.map((point, index) => {
              // Calculer quelle colonne doit afficher le score selon le serveur et le côté
              let col1 = '', col2 = '', col3 = '', col4 = '';
              
              // Pour les conducts, timers et autres décisions spéciales, afficher les deux scores
              const isSpecialDecision = ['CW', 'CS', 'CG', 'LET', 'EQP', 'INJ', 'BLD', 'SHR'].includes(point.decision);
              if (isSpecialDecision) {
                // Afficher le score du Player A dans ses colonnes
                if (point.servingPlayer === 'A') {
                  if (point.servingSide === 'left') {
                    col1 = point.playerA.toString();
                  } else if (point.servingSide === 'right') {
                    col2 = point.playerA.toString();
                  } else {
                    col2 = point.playerA.toString(); // Défaut: droite (cohérent avec la balle de service)
                  }
                }
                // Afficher le score du Player B dans ses colonnes
                if (point.servingPlayer === 'B') {
                  if (point.servingSide === 'left') {
                    col3 = point.playerB.toString();
                  } else if (point.servingSide === 'right') {
                    col4 = point.playerB.toString();
                  } else {
                    col4 = point.playerB.toString(); // Défaut: droite (cohérent avec la balle de service)
                  }
                }
                
                // Pour les conducts, afficher aussi le score de l'adversaire dans sa première colonne
                if (point.decision === 'CS' || point.decision === 'CG') {
                  if (point.servingPlayer === 'A') {
                    col3 = point.playerB.toString(); // Player B score dans sa première colonne
                  } else if (point.servingPlayer === 'B') {
                    col1 = point.playerA.toString(); // Player A score dans sa première colonne
                  }
                }
              } else {
                // Logique normale pour les points réguliers
                if (point.servingPlayer === 'A') {
                  // Player A sert
                  if (point.servingSide === 'left') {
                    col1 = point.playerA.toString(); // Player A sert à gauche → colonne 1
                  } else if (point.servingSide === 'right') {
                    col2 = point.playerA.toString(); // Player A sert à droite → colonne 2
                  } else if (!point.servingSide) {
                    // Si côté de service non défini (null ou undefined), afficher à DROITE par défaut (cohérent avec la balle de service)
                    col2 = point.playerA.toString();
                  }
                } else if (point.servingPlayer === 'B') {
                  // Player B sert
                  if (point.servingSide === 'left') {
                    col3 = point.playerB.toString(); // Player B sert à gauche → colonne 3
                  } else if (point.servingSide === 'right') {
                    col4 = point.playerB.toString(); // Player B sert à droite → colonne 4
                  } else if (!point.servingSide) {
                    // Si côté de service non défini (null ou undefined), afficher à DROITE par défaut (cohérent avec la balle de service)
                    col4 = point.playerB.toString();
                  }
                }
              }
              
              return (
                <tr key={index} className="border-b border-gray-100 last:border-b-0">
                  <td className="py-0.5 px-1 text-center w-1/5">{col1}</td>
                  <td className="py-0.5 px-1 text-center w-1/5">{col2}</td>
                  <td className="py-0.5 px-1 text-center w-1/5">{renderDecision(point)}</td>
                  <td className="py-0.5 px-1 text-center w-1/5">{col3}</td>
                  <td className="py-0.5 px-1 text-center w-1/5">{col4}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Liseret droit - Joueur B */}
      <div className="w-1" style={{ backgroundColor: playerBColor }}></div>
    </div>
  );
}